import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { completionProviders } from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';
import { hasEditableRouting } from './workflow-presentation-authoring';
import type {
  StepAuthoringCommand,
  StepCommand,
} from './workflow-step-authoring.models';

function oneNode(document: WorkflowDefinitionDocument, id: string) {
  const matches = document.nodes.filter((node) => node.id === id);
  if (matches.length !== 1)
    throw new Error(
      'A path destination is missing or ambiguous. Inspect Paths first.',
    );
  return matches[0];
}

function destination(document: WorkflowDefinitionDocument, id: string) {
  const node = oneNode(document, id);
  if (!['human_step', 'automated_step', 'terminal'].includes(node.typeCode))
    throw new Error(
      'Choose an existing work step or completion point as destination.',
    );
  return node;
}

function reaches(
  edges: readonly WorkflowDefinitionEdge[],
  start: string,
  target: string,
) {
  const seen = new Set<string>(),
    pending = [start];
  while (pending.length) {
    const id = pending.pop()!;
    if (id === target) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const edge of edges)
      if (edge.fromNodeId === id && edge.pathKindCode == null)
        pending.push(edge.toNodeId);
  }
  return false;
}

function checkPaths(
  document: WorkflowDefinitionDocument,
  edges: WorkflowDefinitionEdge[],
  changed: WorkflowDefinitionEdge[],
) {
  for (const edge of changed) {
    if (edge.pathKindCode === 'return') {
      if (
        destination(document, edge.toNodeId).typeCode !== 'human_step' ||
        !reaches(edges, edge.toNodeId, edge.fromNodeId)
      )
        throw new Error(
          'A return visit must go to an earlier work step on its forward path.',
        );
    } else if (reaches(edges, edge.toNodeId, edge.fromNodeId)) {
      throw new Error(
        'This path would create a forward cycle. Use a declared return visit for earlier work.',
      );
    }
  }
}

function inspectRoutes(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  command: StepCommand,
  contract: StepAuthoringCommand,
) {
  const condition = contract.condition;
  const saveOnly = contract.completesStep === false;
  if (
    !sameCapabilityReference(command.capability, contract.capability) ||
    !condition ||
    (saveOnly && !contract.decisionEvidence)
  )
    throw new Error(
      'This action has no installed outcome routing contract. Reload actions before editing its paths.',
    );
  const node = oneNode(document, nodeId);
  if (
    !hasEditableRouting(document) ||
    node.typeCode !== 'human_step' ||
    !node.declaration
  )
    throw new Error('Choose one unambiguous editable work step.');
  if (
    new Set(
      node.declaration.behavior.commands.map((item) =>
        capabilityIdentity(item.capability.key),
      ),
    ).size !== node.declaration.behavior.commands.length
  )
    throw new Error(
      'This step has duplicate action identities. Inspect the saved step before editing its paths.',
    );
  const attached = node.declaration.behavior.commands.filter((item) =>
    sameCapabilityReference(item.capability, command.capability),
  );
  if (attached.length !== 1)
    throw new Error(
      'The action is no longer attached or its installed contract has changed.',
    );
  if (
    node.declaration.behavior.commands.some(
      (item) =>
        !sameCapabilityReference(item.capability, command.capability) &&
        item.outcomes.length,
    )
  )
    throw new Error(
      'Multiple actions declare routes. Inspect the paths before replacing shared routing.',
    );
  if (
    saveOnly &&
    attached[0].outcomes.some((outcome) => outcome.destinationNodeId)
  )
    throw new Error(
      'A recorded decision saves its result; destinations belong to step paths. Inspect the saved action before editing it.',
    );
  if (saveOnly) {
    const evidence = contract.decisionEvidence!;
    const requiredCommands = [
      ...(evidence.requiredCommands ?? []),
      ...condition.requiredCapabilities.filter(
        (reference) =>
          reference.key.provider === 'workflow' &&
          reference.key.code === 'workflow.step.complete',
      ),
    ];
    if (
      requiredCommands.some(
        (reference) =>
          node.declaration!.behavior.commands.filter((item) =>
            sameCapabilityReference(item.capability, reference),
          ).length !== 1,
      ) ||
      condition.requiredCapabilities.some(
        (reference) =>
          document.capabilityReferences?.filter((item) =>
            sameCapabilityReference(item, reference),
          ).length !== 1,
      )
    )
      throw new Error(
        'This recorded decision is missing an exact installed companion action or routing dependency. Restore its review setup before editing paths.',
      );
    const bindings =
      node.declaration.behavior.completionRequirements?.filter(
        (requirement) => {
          const reference = requirement.settings?.['sourceCommand'] as
            | StepCommand['capability']
            | undefined;
          return (
            requirement.provider === completionProviders.decision &&
            !!reference?.key &&
            sameCapabilityReference(reference, command.capability)
          );
        },
      ) ?? [];
    const binding = bindings[0],
      accepted = binding?.settings['outcomeCodes'];
    if (
      bindings.length !== 1 ||
      binding.version !== 1 ||
      binding.settingsVersion !== 1 ||
      !binding.settingsHash ||
      Object.keys(binding.settings).some(
        (key) =>
          !['sourceCommand', 'decisionKind', 'outcomeCodes'].includes(key),
      ) ||
      node.declaration.behavior.completionRequirements?.filter(
        (item) => item.id === binding.id,
      ).length !== 1 ||
      binding.settings['decisionKind'] !== evidence.decisionKind ||
      !Array.isArray(accepted) ||
      new Set(accepted).size !== accepted.length ||
      accepted.length !== evidence.outcomeCodes.length ||
      evidence.outcomeCodes.some((code) => !accepted.includes(code)) ||
      command.outcomes.some((outcome) => !accepted.includes(outcome.code))
    )
      throw new Error(
        'The decision completion requirement must accept every installed result before these paths can be used. Edit its accepted results or restore the review setup.',
      );
  }
  if (
    new Set(contract.outcomes.map((outcome) => outcome.code)).size !==
      contract.outcomes.length ||
    new Set(command.outcomes.map((outcome) => outcome.code)).size !==
      command.outcomes.length
  )
    throw new Error(
      'Outcome identities must be unique. Reload actions or inspect the saved action.',
    );
  for (const outcome of command.outcomes) {
    const rule = contract.outcomes.find((item) => item.code === outcome.code);
    if (!rule || !rule.reasonPolicies.includes(outcome.reasonPolicy))
      throw new Error('An outcome has unsupported reason settings.');
  }
  if (
    !command.outcomes.length ||
    contract.outcomes.some(
      (rule) =>
        rule.mandatory &&
        !command.outcomes.some((outcome) => outcome.code === rule.code),
    )
  )
    throw new Error('Include every required outcome.');
  const incoming = document.edges.filter((edge) => edge.fromNodeId === nodeId);
  if (
    incoming.length > 1 ||
    incoming.some(
      (edge) => edge.outcomeCode != null || edge.pathKindCode != null,
    )
  )
    throw new Error(
      'This step has custom paths. Inspect Paths before replacing them.',
    );
  const next = incoming.length
    ? oneNode(document, incoming[0].toNodeId)
    : undefined;
  if (
    next &&
    !['human_step', 'terminal', 'routing_decision'].includes(next.typeCode)
  )
    throw new Error(
      'The current path uses unsupported routing. Inspect Paths first.',
    );
  const current = next?.typeCode === 'routing_decision' ? next : undefined;
  if (
    current &&
    (current.routing?.conditionCode !== condition.code ||
      current.routing.conditionVersion !== condition.version ||
      document.edges.filter((edge) => edge.toNodeId === current.id).length !==
        1)
  )
    throw new Error(
      'This step uses shared or different routing. Inspect Paths before replacing it.',
    );
  const routeId = current?.id ?? `${nodeId}-result`;
  if (
    !current &&
    (document.nodes.some((item) => item.id === routeId) ||
      document.edges.some(
        (edge) => edge.fromNodeId === routeId || edge.toNodeId === routeId,
      ))
  )
    throw new Error(
      'The result node identity is already used. Inspect Paths first.',
    );
  const previous = current
    ? document.edges.filter((edge) => edge.fromNodeId === routeId)
    : [];
  if (
    previous.some(
      (edge) => edge.pathKindCode != null && edge.pathKindCode !== 'return',
    )
  )
    throw new Error(
      'An existing result path has an unsupported path kind. Inspect Paths first.',
    );
  if (
    previous.some(
      (edge) =>
        !command.outcomes.some((outcome) => outcome.code === edge.outcomeCode),
    ) ||
    new Set(previous.map((edge) => edge.outcomeCode)).size !== previous.length
  )
    throw new Error(
      'Existing result paths have additional or duplicate outcomes. Inspect Paths first.',
    );
  for (const edge of [...incoming, ...previous]) {
    if (document.edges.filter((item) => item.id === edge.id).length !== 1)
      throw new Error('A path identity is duplicated. Inspect Paths first.');
  }
  for (const edge of previous) destination(document, edge.toNodeId);
  checkPaths(document, document.edges, previous);
  return {
    node,
    attached: attached[0],
    incoming,
    current,
    routeId,
    previous,
    condition,
    saveOnly,
  };
}

/** Project graph destinations into temporary editor rows; saved decision receipts never own paths. */
export function readStepOutcomeRoutes(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  sourceCommand: StepCommand,
  contract: StepAuthoringCommand,
): StepCommand['outcomes'] {
  const { previous, current, saveOnly } = inspectRoutes(
    document,
    nodeId,
    sourceCommand,
    contract,
  );
  return sourceCommand.outcomes.map((outcome) => {
    const edge = previous.find((item) => item.outcomeCode === outcome.code);
    if (
      current &&
      !saveOnly &&
      outcome.destinationNodeId &&
      outcome.destinationNodeId !== edge?.toNodeId
    )
      throw new Error(
        'The action and graph disagree about an outcome destination. Inspect Paths before editing it.',
      );
    return { ...outcome, destinationNodeId: edge?.toNodeId ?? '' };
  });
}

/** Replace only this action's private result paths, preserving graph identities and extension fields. */
export function applyStepOutcomeRoutes(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  command: StepCommand,
  contract: StepAuthoringCommand,
  returns: ReadonlySet<string>,
): WorkflowDefinitionDocument {
  const { node, incoming, current, routeId, previous, condition, saveOnly } =
    inspectRoutes(document, nodeId, command, contract);
  if (
    [...returns].some(
      (code) => !command.outcomes.some((outcome) => outcome.code === code),
    )
  )
    throw new Error('Returns must name a declared outcome.');
  for (const outcome of command.outcomes)
    destination(document, outcome.destinationNodeId);
  const retained = document.edges.filter(
    (edge) => edge.fromNodeId !== nodeId && edge.fromNodeId !== routeId,
  );
  const replacements: WorkflowDefinitionEdge[] = [
    {
      ...incoming[0],
      id: incoming[0]?.id ?? `${routeId}-enter`,
      fromNodeId: nodeId,
      toNodeId: routeId,
    },
    ...command.outcomes.map((outcome) => {
      const existing = previous.find(
        (edge) => edge.outcomeCode === outcome.code,
      );
      return {
        ...existing,
        id: existing?.id ?? `${routeId}-${outcome.code}`,
        fromNodeId: routeId,
        toNodeId: outcome.destinationNodeId,
        outcomeCode: outcome.code,
        pathKindCode: returns.has(outcome.code) ? 'return' : null,
      };
    }),
  ];
  if (
    new Set(replacements.map((edge) => edge.id)).size !== replacements.length ||
    replacements.some((edge) => retained.some((item) => item.id === edge.id))
  )
    throw new Error(
      'A generated path identity is already used. Inspect Paths first.',
    );
  checkPaths(document, [...retained, ...replacements], replacements);
  let nodes = current
    ? document.nodes
    : [
        ...document.nodes,
        {
          id: routeId,
          typeCode: 'routing_decision',
          displayOrder: node.displayOrder + 1,
          stepCode: null,
          displayName: `${node.displayName || node.stepCode} result`,
          allowedTransitions: null,
          requiredDecisions: null,
          routing: {
            conditionCode: condition.code,
            conditionVersion: condition.version,
          },
        },
      ];
  if (saveOnly)
    nodes = nodes.map((item) =>
      item.id !== nodeId
        ? item
        : {
            ...item,
            declaration: {
              ...node.declaration!,
              behavior: {
                ...node.declaration!.behavior,
                commands: node.declaration!.behavior.commands.map((item) =>
                  !sameCapabilityReference(item.capability, command.capability)
                    ? item
                    : {
                        ...item,
                        outcomes: command.outcomes.map((outcome) => ({
                          ...outcome,
                          destinationNodeId: '',
                        })),
                      },
                ),
              },
            },
          },
    );
  return { ...document, nodes, edges: [...retained, ...replacements] };
}

/** Follow ordinary incoming graph paths and stop at the first work step on each branch. */
export function nearestPreviousWorkSteps(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): WorkflowDefinitionNode[] {
  const source = oneNode(document, nodeId);
  if (source.typeCode !== 'human_step') return [];
  const found = new Map<string, WorkflowDefinitionNode>(),
    visited = new Set<string>(),
    active = new Set<string>([nodeId]);
  const walk = (id: string) => {
    if (active.has(id))
      throw new Error(
        'The preceding paths contain a forward cycle. Inspect Paths before choosing a return step.',
      );
    const node = oneNode(document, id);
    if (node.typeCode === 'human_step') {
      found.set(node.id, node);
      return;
    }
    if (visited.has(id)) return;
    active.add(id);
    for (const edge of document.edges.filter(
      (item) => item.toNodeId === id && item.pathKindCode == null,
    ))
      walk(edge.fromNodeId);
    active.delete(id);
    visited.add(id);
  };
  for (const edge of document.edges.filter(
    (item) => item.toNodeId === nodeId && item.pathKindCode == null,
  ))
    walk(edge.fromNodeId);
  return [...found.values()];
}
